import { Component, DestroyRef, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FixturePage } from '@cds/betting-offer';
import { DayOfRaceDate, TimeGroupedMeetings } from '@cds/betting-offer/domain-specific/horse-racing';
import { Meeting, TagCount } from '@cds/betting-offer/tags';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { translationToStr } from '@frontend/sports/common/core/utils/translations';
import { TournamentGroup } from '@frontend/sports/racing/feature/models';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { HtmlNode } from '@frontend/vanilla/core';

import { convertDayOfRaceToUrlParam } from '../../../horse-racing-shared/helpers';
import { RedirectHelperService } from '../../../navigation-core/redirect-helper.service';
import { UrlHelperService } from '../../../navigation-core/url-helper.service';
import { Events } from '../../models/events';

@Component({
    template: '',
    standalone: false,
})
export class TournamentBaseComponent implements OnDestroy {
    tournamentGroups: TournamentGroup[] = [];
    isOpen = false;
    protected readonly destroyRef = inject(DestroyRef);

    constructor(
        protected dipatcher: DispatcherService,
        protected redirectHelper: RedirectHelperService,
        protected trackingService: TrackingService,
        protected sitecore: Sitecore,
        protected htmlNode: HtmlNode,
        protected urlHelper: UrlHelperService,
    ) {}

    goToTournament(tagCount: TagCount, dayOfRaceDate: DayOfRaceDate): void {
        const meeting = tagCount.tag as Meeting;
        this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, {
            [trackingConstants.PAGE_NAME]: `Meeting/Overview/${meeting.id}`,
        });

        this.redirectHelper.goToHorseRacing(
            {
                id: meeting.id,
                name: translationToStr(meeting.name),
            },
            convertDayOfRaceToUrlParam(dayOfRaceDate),
        );
        this.dipatcher.dispatch(Events.HorseRacing.MeetingSelected, { meeting, dayOfRaceDate });
    }

    private openHorseTournamentSelector(data: {
        meetings: TimeGroupedMeetings;
        dayOfRace: DayOfRaceDate;
        meeting: Meeting;
        futureMeetings: FixturePage;
    }): void {
        this.tournamentGroups = [];
        this.tournamentGroups.push({
            id: DayOfRaceDate.Today,
            //TODO any index issue, probably a defect, please investigate tournaments assignment, remove as any to see the error
            tournaments: (<TagCount[]>data.meetings.byDay[DayOfRaceDate.Today.toLowerCase() as keyof typeof data.meetings.byDay]).map((t) => ({
                meeting: t,
                attributes: data.meeting
                    ? {
                          selected: t.tag.id === data.meeting.id && data.dayOfRace === DayOfRaceDate.Today,
                      }
                    : data.meetings.byDay[DayOfRaceDate.Today.toLowerCase() as keyof typeof data.meetings.byDay],
            })) as any,
            expanded: data.meeting ? data.dayOfRace === DayOfRaceDate.Today : true,
        });
        this.tournamentGroups.push({
            id: DayOfRaceDate.Tomorrow,
            //TODO any index issue, probably a defect, please investigate tournaments assignment, remove as any to see the error
            tournaments: data.meeting
                ? (<TagCount[]>data.meetings.byDay[DayOfRaceDate.Tomorrow.toLowerCase() as keyof typeof data.meetings.byDay]).map((t) => ({
                      meeting: t,
                      attributes: {
                          selected: t.tag.id === data.meeting.id && data.dayOfRace === DayOfRaceDate.Tomorrow,
                      },
                  }))
                : (data.meetings.byDay[DayOfRaceDate.Tomorrow.toLowerCase() as keyof typeof data.meetings.byDay] as any),
            expanded: data.dayOfRace === DayOfRaceDate.Tomorrow,
        });
        this.tournamentGroups.push({
            id: 'Future',
            expanded: false,
            tournaments: data.futureMeetings['fixtures'].map((f) => ({
                meeting: f.meeting,
                url: this.urlHelper.getFixtureUrlByFixture(f),
            })),
        });

        this.htmlNode.setCssClass('no-scrolling', true);
        this.isOpen = true;
    }

    toggleGroup(tournamentGroup: TournamentGroup): void {
        tournamentGroup.expanded = !tournamentGroup.expanded;
    }

    translateTournamentGroup(tournamentGroup: DayOfRaceDate): string {
        return this.sitecore.horseRacing.navigation[tournamentGroup] || tournamentGroup;
    }

    private closeHorseTournamentSelector(): void {
        this.htmlNode.setCssClass('no-scrolling', false);
        this.isOpen = false;
    }

    dipatcherHorseRacing() {
        this.dipatcher
            .on(Events.HorseRacing.OpenMeetingSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
                this.openHorseTournamentSelector(data);
            });

        this.dipatcher
            .on(Events.HorseRacing.CloseMeetingSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.closeHorseTournamentSelector());
    }

    ngOnDestroy(): void {
        this.htmlNode.setCssClass('no-scrolling', false);
    }
}
